import type { FC } from 'react';
import React, { useMemo } from 'react';
import { TransactionSourceType } from '@pflegenavi/shared/api';
import {
  useShowResidentForFamilyMemberPhoenix,
  useTransactionsForFamilyMemberPhoenix,
  useGetPaymentInfoForFamilyMemberPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { useFmPaymentsTable } from '../hooks/useFmPaymentsTable';

import { useInitializeFM } from '../hooks/useInitializeFM';
import {
  getActiveBalanceId,
  RESIDENT_WALLET,
} from '@pflegenavi/shared-frontend/platform';
import { PaymentsPageView, type SearchablePayment } from './PaymentsPageView';
import { getPaymentViewData } from '@pflegenavi/shared/frontend';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface PaymentsPageProps {}

// We don't want to create a new object every time the component is rendered
const sourceTypes = [
  TransactionSourceType.InitialDeposit,
  TransactionSourceType.ManualDeposit,
  TransactionSourceType.Payout,
  TransactionSourceType.CancelledManualDeposit,
  TransactionSourceType.DirectDebit,
  TransactionSourceType.FinalSettlement,
  TransactionSourceType.Dispute,
];

export const PaymentsPage: FC<PaymentsPageProps> = () => {
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();

  const { data: residentResultPhoenix } = useShowResidentForFamilyMemberPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  const activeBalanceId = useMemo(() => {
    return getActiveBalanceId(residentResultPhoenix?.data?.resident);
  }, [residentResultPhoenix]);

  // Get payment info for pending charges
  const { data: paymentInfoPhoenix } = useGetPaymentInfoForFamilyMemberPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  // Get pending charges data from payment info
  const pendingCharges = paymentInfoPhoenix?.pending_charges ?? [];

  const currentBalance = useMemo(() => {
    return residentResultPhoenix?.data?.resident?.balance?.amount || 0;
  }, [residentResultPhoenix]);

  const {
    isLoading,
    isError,
    data: payments,
  } = useTransactionsForFamilyMemberPhoenix(
    selectedResident?.residentId,
    selectedResident?.tenant,
    sourceTypes,
    activeBalanceId === RESIDENT_WALLET ? null : activeBalanceId
  );

  const tableData = useFmPaymentsTable();
  const { filterTerms, setFilterTerms, reportingYear, setReportingYear } =
    tableData;

  const nursingHomeName = useMemo(() => {
    return (
      residentResultPhoenix?.data?.resident?.nursing_home?.name ||
      'Nursing Home'
    );
  }, [residentResultPhoenix]);

  const searchablePayments: SearchablePayment[] | undefined = useMemo(() => {
    return payments?.map((payment) => {
      const viewData = getPaymentViewData(payment, nursingHomeName, t);

      return {
        id: payment.id,
        sourceType: payment.sourceType,
        sourceTypeString: viewData.typeText,
        type: payment.type,
        serviceProvider: payment.serviceProvider,
        date: payment.date,
        initiationDate: payment.initiationDate,
        amount: payment.amount,
        paymentStateString: viewData.statusText,
        disputed: payment.disputed,
        initiatedBy: payment.initiatedBy,
        initiatedByName: viewData.initiatorName,
        cancelled: payment.cancelled,
        ibanLast4: payment.ibanLast4,
        sequenceID: payment.sequenceID,
      };
    });
  }, [payments, nursingHomeName, t]);

  return (
    <PaymentsPageView
      isResidentPending={!residentResultPhoenix}
      isPaymentsLoading={isLoading}
      isPaymentsError={isError}
      currentBalance={currentBalance}
      pendingCharges={pendingCharges}
      payments={searchablePayments}
      nursingHomeName={nursingHomeName}
      tableProps={tableData}
      filterTerms={filterTerms}
      setFilterTerms={setFilterTerms}
      reportingYear={reportingYear}
      setReportingYear={setReportingYear}
    />
  );
};
